import { useEffect, useMemo, useRef, useState } from "react";

import { date, money, pct } from "../format";

/**
 * An equity curve: one series, one axis, a crosshair.
 *
 * Built to the dataviz rules: a 2px line in the brand accent (single series,
 * so the card title names it and there is no legend), recessive grid and
 * axes, a dashed reference at the starting value, and a crosshair that snaps
 * to the nearest bar with a tooltip — on pointer and on keyboard (focus the
 * chart, use ← →). Text is always text ink, never the line's colour.
 */
interface Props {
  points: Array<[number, number]>;
  baseline?: number;
  height?: number;
  label: string;
  /** Show times, not just dates, in the tooltip (intraday bots). */
  intraday?: boolean;
}

const PAD = { top: 12, right: 12, bottom: 26, left: 56 };

function niceTicks(min: number, max: number, count = 4): number[] {
  if (!(max > min)) return [min];
  const raw = (max - min) / count;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= raw) ?? raw;
  const out: number[] = [];
  for (let v = Math.ceil(min / step) * step; v <= max + 1e-9; v += step) out.push(v);
  return out;
}

export function EquityChart({ points, baseline, height = 260, label, intraday = false }: Props) {
  const wrap = useRef<HTMLDivElement | null>(null);
  // The wrapper as state, not only a ref: it doesn't exist while there are no
  // points yet, and an observer set up once on mount then never attached — the
  // chart stayed 640px wide and was cut off on every phone.
  const [el, setEl] = useState<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(640);
  const [hover, setHover] = useState<number | null>(null);

  useEffect(() => {
    if (!el) return;
    const ro = new ResizeObserver(([e]) => e && setWidth(Math.max(240, Math.floor(e.contentRect.width))));
    ro.observe(el);
    return () => ro.disconnect();
  }, [el]);

  const geo = useMemo(() => {
    if (points.length === 0) return null;
    const xs = points.map((p) => p[0]);
    const ys = points.map((p) => p[1]);
    let yMin = Math.min(...ys, baseline ?? Infinity);
    let yMax = Math.max(...ys, baseline ?? -Infinity);
    const padY = (yMax - yMin) * 0.08 || Math.abs(yMax) * 0.01 || 1;
    yMin -= padY;
    yMax += padY;
    const x0 = xs[0]!;
    const x1 = xs[xs.length - 1]! === x0 ? x0 + 1 : xs[xs.length - 1]!;
    const iw = width - PAD.left - PAD.right;
    const ih = height - PAD.top - PAD.bottom;
    const sx = (x: number) => PAD.left + ((x - x0) / (x1 - x0)) * iw;
    const sy = (y: number) => PAD.top + (1 - (y - yMin) / (yMax - yMin)) * ih;
    const path = points
      .map((p, i) => `${i ? "L" : "M"}${sx(p[0]).toFixed(1)},${sy(p[1]).toFixed(1)}`)
      .join("");
    const yTicks = niceTicks(yMin, yMax);
    // As many date labels as fit. A label ("Mar 26, 25") is about 70px, and the
    // end labels hang inward from the edges, so neighbours need ~1.5 labels
    // between them. Five, always, ran into each other on a phone.
    const nx = Math.max(2, Math.min(5, Math.floor(iw / 115) + 1));
    const xTicks = Array.from({ length: nx }, (_, i) => x0 + ((x1 - x0) * i) / (nx - 1));
    return { sx, sy, path, yTicks, xTicks, iw, ih, x0, x1 };
  }, [points, width, height, baseline]);

  if (!geo) return <p className="muted">No equity recorded yet.</p>;

  const nearest = (clientX: number) => {
    const rect = wrap.current!.getBoundingClientRect();
    const x = clientX - rect.left;
    const t = geo.x0 + ((x - PAD.left) / geo.iw) * (geo.x1 - geo.x0);
    let lo = 0;
    let hi = points.length - 1;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (points[mid]![0] < t) lo = mid + 1;
      else hi = mid;
    }
    if (lo > 0 && Math.abs(points[lo - 1]![0] - t) < Math.abs(points[lo]![0] - t)) lo -= 1;
    return lo;
  };

  const h = hover !== null ? points[hover] : null;
  const first = points[0]![1];
  const ref = baseline ?? first;

  return (
    <div
      className="equity"
      ref={(node) => {
        wrap.current = node;
        setEl(node);
      }}
    >
      <svg
        width={width}
        height={height}
        role="img"
        aria-label={`${label}: from ${money(first)} to ${money(points[points.length - 1]![1])}`}
        tabIndex={0}
        onPointerMove={(e) => setHover(nearest(e.clientX))}
        onPointerLeave={() => setHover(null)}
        onBlur={() => setHover(null)}
        onKeyDown={(e) => {
          if (e.key === "ArrowLeft") setHover((v) => Math.max(0, (v ?? points.length) - 1));
          if (e.key === "ArrowRight") setHover((v) => Math.min(points.length - 1, (v ?? -1) + 1));
        }}
      >
        {geo.yTicks.map((v) => (
          <g key={v}>
            <line
              x1={PAD.left}
              x2={width - PAD.right}
              y1={geo.sy(v)}
              y2={geo.sy(v)}
              className="equity__grid"
            />
            <text
              x={PAD.left - 8}
              y={geo.sy(v)}
              className="equity__tick"
              textAnchor="end"
              dominantBaseline="middle"
            >
              {money(v)}
            </text>
          </g>
        ))}
        {geo.xTicks.map((t, i) => (
          <text
            key={t}
            x={geo.sx(t)}
            y={height - 8}
            className="equity__tick"
            textAnchor={i === 0 ? "start" : i === geo.xTicks.length - 1 ? "end" : "middle"}
          >
            {intraday && geo.x1 - geo.x0 < 3 * 86_400_000
              ? new Date(t).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
              : new Date(t).toLocaleDateString([], {
                  month: "short",
                  day: "numeric",
                  year: geo.x1 - geo.x0 > 300 * 86_400_000 ? "2-digit" : undefined,
                })}
          </text>
        ))}
        {baseline != null && (
          <line
            x1={PAD.left}
            x2={width - PAD.right}
            y1={geo.sy(baseline)}
            y2={geo.sy(baseline)}
            className="equity__base"
          />
        )}
        <path d={geo.path} className="equity__line" />
        {h && (
          <g>
            <line
              x1={geo.sx(h[0])}
              x2={geo.sx(h[0])}
              y1={PAD.top}
              y2={height - PAD.bottom}
              className="equity__cross"
            />
            <circle cx={geo.sx(h[0])} cy={geo.sy(h[1])} r={4.5} className="equity__dot" />
          </g>
        )}
      </svg>
      {h && (
        <div
          className="equity__tip"
          style={{ left: Math.min(Math.max(geo.sx(h[0]), 90), width - 90), top: 4 }}
          role="status"
        >
          <strong>{money(h[1], 2)}</strong>
          <span className={h[1] >= ref ? "up" : "down"}>{pct((h[1] / ref - 1) * 100, 2)}</span>
          <span className="muted">{date(h[0], intraday)}</span>
        </div>
      )}
    </div>
  );
}

/** A tiny trend line for cards. Decorative: the numbers beside it carry the meaning. */
export function Sparkline({
  points,
  width = 120,
  height = 32,
}: {
  points: Array<[number, number]>;
  width?: number;
  height?: number;
}) {
  if (points.length < 2)
    return <span className="spark spark--empty" style={{ width, height }} aria-hidden="true" />;
  const xs = points.map((p) => p[0]);
  const ys = points.map((p) => p[1]);
  const [x0, x1] = [Math.min(...xs), Math.max(...xs)];
  const [y0, y1] = [Math.min(...ys), Math.max(...ys)];
  const d = points
    .map(
      (p, i) =>
        `${i ? "L" : "M"}${(((p[0] - x0) / (x1 - x0 || 1)) * (width - 2) + 1).toFixed(1)},${((1 - (p[1] - y0) / (y1 - y0 || 1)) * (height - 4) + 2).toFixed(1)}`,
    )
    .join("");
  return (
    <svg className="spark" width={width} height={height} aria-hidden="true">
      <path d={d} />
    </svg>
  );
}
